import makeStyles from '@mui/styles/makeStyles';
import {history} from "common/history";
import {color, font, radius, type} from "common/tokens";
import CtaButton from "components/redesign/CtaButton";
import Dialog from "components/redesign/Dialog";
import Question from "components/Question";
import Skeleton from "components/redesign/Skeleton";
import questionFields from "components/redesign/questionFields";
import {flatten, get, join, sortBy} from "lodash";
import React from "react";
import {useTranslation} from "react-i18next";

const useStyles = makeStyles({
  count: {
    ...type.meta,
    background: color.ACCENT_TINT,
    color: color.ACCENT_TEXT,
    borderRadius: radius.PILL,
    padding: '5px 14px',
    margin: 0,
  },
  spacer: {
    flex: 1,
  },
  loading: {
    ...type.body,
    color: color.TEXT_MUTED,
    margin: 0,
  },
  // Holds the shape of the question and its field, so the challenge arrives
  // where the reader is already looking instead of behind a second dialog.
  placeholder: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  question: questionFields,
});

const quizIsMandatory = quiz =>
  flatten(get(quiz, 'questions', [])).some(question => question.mandatory === true);

function SessionQuiz({
  location,
  quiz,
  session,
  currentPage,
  nextPage,
  previousPage,
  quizError,
  currentStepQuizAnswers,
  setSessionQuizAnswer,
  submitQuiz
}) {
  const classes = useStyles();
  const {t} = useTranslation();
  const {stepId, quizId} = (location && location.state) || {};

  const close = () => history.goBack();

  if (!quiz || !Array.isArray(quiz.questions)) {
    // Same shell as the loaded challenge, so nothing jumps when it arrives.
    // The "may be unavailable" line only earns its place once a request has
    // actually come back empty — before that it reads as a failure.
    return (
      <Dialog title={t('takeQuizOptional')} onClose={close}>
        {quizError
          ? <p className={classes.loading}>{t('quizLoadingOrNotFound')}</p>
          : (
            <div className={classes.placeholder}>
              <Skeleton width={'70%'} height={28}/>
              <Skeleton height={120}/>
            </div>
          )}
      </Dialog>
    );
  }

  const {questions} = quiz;
  const currentQuizAnswers = get(currentStepQuizAnswers, `[${session.id}][${stepId}].answers`);

  const orderedQuestions = sortBy(questions, question => question.order);
  const numberOfPages = orderedQuestions.length;
  const isLastPage = currentPage === numberOfPages - 1;
  const isFirstPage = currentPage === 0;
  const currentQuestion = get(orderedQuestions, `[${currentPage}]`);
  const isMandatory = quizIsMandatory(quiz);

  const getErrorFor = question => join(get(quizError, question.key), '\n');

  // The question carries the screen, so it is set larger and in the body text
  // colour rather than the muted 12px the shared label defaults to.
  const questionLabelStyling = {
    fontSize: 20,
    lineHeight: '28px',
    fontWeight: font.SEMIBOLD,
    color: color.TEXT,
  };

  const questionLabelSpacing = {marginTop: 0, paddingBottom: 20};

  const footer = (
    <>
      {!isFirstPage && (
        <CtaButton
          size={'control'}
          variant={'secondary'}
          label={`← ${t('previous')}`}
          onClick={() => previousPage()}
        />
      )}
      <span className={classes.spacer}/>
      <p className={classes.count}>{`${currentPage + 1}/${numberOfPages}`}</p>
      <CtaButton
        size={'large'}
        variant={'challenge'}
        required={isMandatory}
        label={isLastPage ? t('submit') : t('next')}
        onClick={() => isLastPage
          ? submitQuiz(questions, session, stepId, quizId, currentQuizAnswers, currentQuestion)
          : nextPage(orderedQuestions[currentPage], currentQuizAnswers)}
      />
    </>
  );

  return (
    <Dialog
      onClose={close}
      title={isMandatory ? t('takeQuizMandatory') : t('takeQuizOptional')}
      progress={((currentPage + 1) * 100) / numberOfPages}
      footer={footer}
    >
      <div className={classes.question}>
        <Question
          key={currentQuestion.key}
          question={currentQuestion}
          labelStyling={questionLabelStyling}
          labelSpacing={questionLabelSpacing}
          onAnswered={answer => setSessionQuizAnswer(stepId, currentQuestion, answer)}
          value={get(currentQuizAnswers, currentQuestion.key)}
          error={getErrorFor(currentQuestion)}
          variant={'rows'}
          showBorder
        />
      </div>
    </Dialog>
  );
}

export default SessionQuiz;
